/**
 * Homepage — repositioned 2026-07-23 for the provider-led peptide therapy
 * platform launch.
 *
 * Section order per stakeholder spec:
 *   1. Hero (Medical-Grade Peptide Therapy Starts Here)
 *   2. Trust Indicators row
 *   3. Featured Peptide Therapies (Treatment Categories — 10 cards)
 *   4. Conditions We Commonly Treat
 *   5. How It Works (5-step provider-led process)
 *   6. Meet Our Providers (grid, links to /providers)
 *   7. Patient Testimonials (only when we have published testimonials)
 *   8. Educational Resources (published articles)
 *   9. Frequently Asked Questions
 *  10. Final Schedule Consultation banner
 *
 * Uses visit-type-agnostic language ("meet with a licensed medical provider")
 * that works for in-person AND telemedicine visits. Never implies
 * telehealth-only.
 */
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  ArrowRight, ShieldCheck, MapPin, ClipboardList, Stethoscope,
  MessageSquare, Package, HeartPulse, Activity, TrendingUp,
  Sparkles, Zap, Brain, Droplets, FlaskConical, Users2,
} from "lucide-react";
import Disclaimer from "../components/Disclaimer";
import SEO from "../components/SEO";
import { ScheduleConsultationCTA } from "../components/ScheduleConsultationCTA";
import HomeLocationsSection from "../components/HomeLocationsSection";
import api from "../lib/api";

const TRUST_INDICATORS = [
  "Licensed Medical Providers",
  "Personalized Treatment Plans",
  "Multiple Clinic Locations",
  "HIPAA Secure",
  "Evidence-Based Care",
  "Ongoing Provider Support",
];

// Ten featured treatment categories per stakeholder brief.
const TREATMENT_CATEGORIES = [
  { slug: "recovery-performance",  label: "Recovery",             icon: Activity,     tint: "from-teal-50 to-white" },
  { slug: "longevity",             label: "Longevity",            icon: TrendingUp,   tint: "from-sky-50 to-white" },
  { slug: "medical-weight-management", label: "Weight Management", icon: Zap,         tint: "from-amber-50 to-white" },
  { slug: "muscle-gain",           label: "Body Composition",     icon: Users2,       tint: "from-emerald-50 to-white" },
  { slug: "longevity",             label: "Healthy Aging",        icon: Sparkles,     tint: "from-purple-50 to-white" },
  { slug: "andropause",            label: "Hormone Optimization", icon: HeartPulse,   tint: "from-rose-50 to-white" },
  { slug: "aesthetic-skin",        label: "Hair & Skin",          icon: Droplets,     tint: "from-pink-50 to-white" },
  { slug: "immune-gut",            label: "Inflammation Support", icon: FlaskConical, tint: "from-lime-50 to-white" },
  { slug: "recovery-performance",  label: "Performance",          icon: Activity,     tint: "from-cyan-50 to-white" },
  { slug: "longevity",             label: "Cognitive Health",     icon: Brain,        tint: "from-indigo-50 to-white" },
];

// 5-step "How it works" — provider-first, visit-type-agnostic.
const HOW_IT_WORKS = [
  { title: "Browse Peptide Therapies", desc: "Explore the medical peptide catalog to see the education, evidence, and candidate criteria for each therapy." },
  { title: "Complete Secure Medical Intake", desc: "Answer a short HIPAA-secure medical questionnaire so your provider can prepare for your consultation." },
  { title: "Meet With a Licensed Provider", desc: "Consult in person at a participating clinic or via telemedicine where legally appropriate." },
  { title: "Personalized Treatment Plan", desc: "If clinically appropriate, receive a personalized plan and have therapy coordinated through appropriate pharmacy channels." },
  { title: "Ongoing Follow-Up", desc: "Schedule follow-up visits, lab work, and medical monitoring for as long as therapy continues." },
];

const HOMEPAGE_FAQ = [
  { q: "Do I have to visit a clinic in person, or is telemedicine an option?",
    a: "Both. We support in-person visits at participating clinics and telemedicine where it is legally appropriate for your state and clinical scenario. Your provider will confirm the visit type that best fits your situation." },
  { q: "Do you sell peptides directly to consumers?",
    a: "No. MedicalPeptides.ai is a provider-led platform. All peptide therapy is dispensed through appropriate pharmacy channels only after a licensed provider determines that a treatment is clinically appropriate for you." },
  { q: "Do I need a referral or existing prescription to book?",
    a: "No. Your consultation with our provider is the starting point. Bring any recent lab work, medication lists, and specific goals — our providers work with you from there." },
  { q: "How much does peptide therapy cost?",
    a: "Because every treatment plan is individualized, costs vary. Our pricing page explains the philosophy, and your provider reviews all costs with you before therapy begins." },
  { q: "Is peptide therapy safe?",
    a: "Peptide therapy carries risks like any medical intervention. Safety depends on your medical history, the specific therapy, appropriate monitoring, and provider oversight — which is exactly why every treatment starts with a licensed provider evaluation." },
  { q: "Which peptides are FDA-approved?",
    a: "A small number of peptide medications (like tesamorelin, semaglutide, and tirzepatide) are FDA-approved for specific indications. Many other peptides discussed on this site are considered off-label, compounded, or investigational. Every therapy page states the regulatory status clearly." },
];

export default function Home() {
  const [publishedArticles, setPublishedArticles] = useState([]);
  const [publishedConditions, setPublishedConditions] = useState([]);
  const [providers, setProviders] = useState([]);

  useEffect(() => {
    // Non-blocking fetches; homepage still renders if any of these fail.
    Promise.all([
      api.get("/content/article").catch(() => ({ data: [] })),
      api.get("/content/condition").catch(() => ({ data: [] })),
      api.get("/content/provider").catch(() => ({ data: [] })),
    ]).then(([a, c, p]) => {
      const artList = Array.isArray(a.data) ? a.data : (a.data?.items || []);
      const condList = Array.isArray(c.data) ? c.data : (c.data?.items || []);
      const provList = Array.isArray(p.data) ? p.data : (p.data?.items || []);
      setPublishedArticles(artList.slice(0, 6));
      setPublishedConditions(condList.slice(0, 6));
      setProviders(provList.slice(0, 4));
    });
  }, []);

  return (
    <div data-testid="home-page">
      <SEO
        title="Medical Peptides — America's Trusted Medical Peptide Provider"
        description="Provider-led peptide therapy. Meet with one of our licensed medical providers to determine whether medical-grade peptide therapy is right for you. In-person and telemedicine where legally appropriate."
        path="/"
      />

      {/* ============ 1. HERO ============ */}
      <section className="vv-hero" data-testid="home-hero">
        <div className="vv-container py-16 md:py-24 grid md:grid-cols-[1.15fr_1fr] gap-12 items-center">
          <div>
            <div className="vv-hero-eyebrow mb-4 flex items-center gap-2">
              <ShieldCheck size={14} /> America's Trusted Medical Peptide Provider
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display tracking-tight leading-[1.05]">
              Medical-Grade Peptide Therapy Starts Here
            </h1>
            <p className="mt-6 text-lg leading-relaxed max-w-xl">
              Meet with a licensed medical provider to determine whether
              peptide therapy is right for you. If approved, receive a
              personalized treatment plan and ongoing medical support.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <ScheduleConsultationCTA
                location="home-hero-primary"
                variant="gold"
                size="lg"
              >
                Start Your Consultation
              </ScheduleConsultationCTA>
              <Link
                to="/peptide-library"
                data-testid="hero-browse-therapies"
                className="vv-btn-outline"
              >
                Browse Peptide Therapies <ArrowRight size={16} />
              </Link>
            </div>
            <p className="mt-6 text-sm max-w-xl opacity-80">
              All treatment decisions are made by licensed medical providers
              after evaluation. Individual results and eligibility vary.
            </p>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 bg-amber-50 rounded-3xl -z-10" />
            <img
              src="https://images.unsplash.com/photo-1518611012118-696072aa579a?w=1600&auto=format&fit=crop"
              alt="Adult practicing self-care outdoors — health and vitality"
              className="rounded-2xl object-cover w-full h-[480px] shadow-sm"
            />
            <div className="absolute -bottom-6 -left-6 bg-white text-stone-900 rounded-2xl border border-stone-200 p-4 shadow-lg max-w-[260px] hidden md:block">
              <div className="text-xs uppercase tracking-widest text-stone-500">Provider-led</div>
              <div className="font-display text-lg mt-1 text-stone-900">
                Licensed medical providers overseeing every plan
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ 2. TRUST INDICATORS ============ */}
      <section
        data-testid="home-trust-indicators"
        className="bg-white border-y border-stone-200"
      >
        <div className="vv-container py-6">
          <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-y-3 gap-x-6 text-sm text-stone-700">
            {TRUST_INDICATORS.map((label) => (
              <li key={label} className="flex items-center gap-2">
                <ShieldCheck size={16} className="text-[color:var(--vv-teal)] shrink-0" />
                <span className="font-medium">{label}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ============ 3. FEATURED TREATMENT CATEGORIES ============ */}
      <section className="vv-container py-20" data-testid="home-treatment-categories">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="vv-hero-eyebrow mb-2">Featured Peptide Therapies</div>
            <h2 className="font-display text-3xl md:text-4xl">
              Explore treatment categories.
            </h2>
          </div>
          <Link
            to="/peptide-library"
            data-testid="home-shop-therapies"
            className="text-sm font-semibold text-[color:var(--vv-teal)] hover:underline"
          >
            Shop Peptide Therapies →
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {TREATMENT_CATEGORIES.map((cat, i) => {
            const Icon = cat.icon;
            return (
              <Link
                key={`${cat.label}-${i}`}
                to={`/treatment-goals/${cat.slug}`}
                data-testid={`home-category-${cat.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`rounded-2xl border border-stone-200 bg-gradient-to-br ${cat.tint} p-5 hover:shadow-md transition-shadow`}
              >
                <div className="w-10 h-10 rounded-full bg-white border border-stone-200 flex items-center justify-center text-[color:var(--vv-teal)] mb-3">
                  <Icon size={18} />
                </div>
                <div className="font-display text-lg text-stone-900">{cat.label}</div>
                <div className="text-xs text-stone-500 mt-1">Provider evaluation required</div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* ============ 4. CONDITIONS WE COMMONLY TREAT ============ */}
      {publishedConditions.length > 0 ? (
        <section
          data-testid="home-conditions-we-treat"
          className="bg-[color:var(--vv-alt)] border-y border-stone-200"
        >
          <div className="vv-container py-20">
            <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
              <div>
                <div className="vv-hero-eyebrow mb-2">Conditions we commonly evaluate</div>
                <h2 className="font-display text-3xl md:text-4xl">
                  Evidence-based care for the conditions patients ask us about most.
                </h2>
              </div>
              <Link
                to="/conditions"
                className="text-sm font-semibold text-[color:var(--vv-teal)] hover:underline"
              >
                See all conditions →
              </Link>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {publishedConditions.map((c) => (
                <Link
                  key={c.slug}
                  to={`/conditions/${c.slug}`}
                  data-testid={`home-condition-${c.slug}`}
                  className="rounded-2xl bg-white border border-stone-200 p-6 hover:shadow-md transition-shadow"
                >
                  <div className="font-display text-lg text-stone-900">{c.title}</div>
                  {c.subtitle ? (
                    <p className="text-sm text-stone-600 mt-2 line-clamp-2">{c.subtitle}</p>
                  ) : null}
                  <div className="text-xs text-[color:var(--vv-teal)] font-semibold mt-3 flex items-center gap-1">
                    Learn more <ArrowRight size={12} />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {/* ============ 5. HOW IT WORKS ============ */}
      <section
        data-testid="home-how-it-works"
        className="vv-container py-24"
      >
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="vv-hero-eyebrow">How it works</div>
          <h2 className="font-display text-4xl md:text-5xl mt-3 leading-[1.05]">
            From consultation to personalized care in five steps.
          </h2>
        </div>
        <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 lg:gap-4">
          {HOW_IT_WORKS.map((s, i) => {
            const icons = [Package, ClipboardList, Stethoscope, MessageSquare, ShieldCheck];
            const Icon = icons[i];
            return (
              <li
                key={s.title}
                data-testid={`home-step-${i + 1}`}
                className="relative flex flex-col"
              >
                <div className="w-14 h-14 rounded-full bg-[color:var(--vv-teal)] text-white grid place-items-center font-display font-black text-lg mb-4">
                  {i + 1}
                </div>
                <Icon size={22} className="text-[color:var(--vv-teal)] mb-2" />
                <div className="font-display font-bold text-[color:var(--vv-ink)] text-lg leading-snug">
                  {s.title}
                </div>
                <p className="mt-2 text-sm text-stone-600 leading-relaxed">{s.desc}</p>
              </li>
            );
          })}
        </ol>
        <div className="mt-10 text-center">
          <ScheduleConsultationCTA location="home-how-it-works-cta" size="lg" />
        </div>
      </section>

      {/* ============ 6. MEET OUR PROVIDERS ============ */}
      {providers.length > 0 ? (
        <section
          data-testid="home-providers"
          className="bg-[color:var(--vv-ink)] text-white"
        >
          <div className="vv-container py-20">
            <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-[color:var(--vv-teal)] font-semibold">
                  Meet our providers
                </div>
                <h2 className="font-display text-3xl md:text-4xl mt-2">
                  Licensed clinicians, evidence-based care.
                </h2>
              </div>
              <Link
                to="/providers"
                className="text-sm font-semibold text-[color:var(--vv-teal)] hover:underline"
              >
                See all providers →
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {providers.map((p) => (
                <Link
                  key={p.slug}
                  to={`/providers/${p.slug}`}
                  data-testid={`home-provider-${p.slug}`}
                  className="rounded-2xl bg-white/5 hover:bg-white/10 transition-colors p-5 border border-white/10"
                >
                  {p.featured_image ? (
                    <img
                      src={p.featured_image}
                      alt={p.title}
                      className="w-full h-40 rounded-xl object-cover mb-3"
                    />
                  ) : (
                    <div className="w-full h-40 rounded-xl bg-white/10 grid place-items-center mb-3">
                      <Stethoscope size={28} className="text-white/50" />
                    </div>
                  )}
                  <div className="font-display text-lg">{p.title}</div>
                  {p.subtitle ? (
                    <div className="text-xs text-stone-300 mt-1 line-clamp-2">{p.subtitle}</div>
                  ) : null}
                </Link>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {/* ============ 7. TESTIMONIALS (only when we have them) ============ */}
      {/* Testimonials page still exists; homepage teases it once we have
          verified patient stories. Skip section entirely for now to stay
          compliant while the testimonial system is finalized. */}

      {/* ============ 8. EDUCATIONAL RESOURCES ============ */}
      {publishedArticles.length > 0 ? (
        <section className="vv-container py-20" data-testid="home-articles">
          <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
            <div>
              <div className="vv-hero-eyebrow mb-2">Educational resources</div>
              <h2 className="font-display text-3xl md:text-4xl">
                Peptide science in plain language.
              </h2>
            </div>
            <Link
              to="/research"
              className="text-sm font-semibold text-[color:var(--vv-teal)] hover:underline"
            >
              Browse all resources →
            </Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {publishedArticles.map((a) => (
              <Link
                key={a.slug}
                to={`/articles/${a.slug}`}
                data-testid={`home-article-${a.slug}`}
                className="rounded-2xl border border-stone-200 bg-white hover:shadow-md transition-shadow overflow-hidden"
              >
                {a.featured_image ? (
                  <img
                    src={a.featured_image}
                    alt={(a.extra && a.extra.featured_image_alt) || a.title}
                    className="w-full h-48 object-cover"
                    loading="lazy"
                  />
                ) : null}
                <div className="p-5">
                  <div className="font-display text-lg leading-tight text-stone-900">
                    {a.title}
                  </div>
                  {a.meta_description ? (
                    <p className="text-sm text-stone-600 mt-2 line-clamp-2">
                      {a.meta_description}
                    </p>
                  ) : null}
                  <div className="text-xs text-[color:var(--vv-teal)] font-semibold mt-3 flex items-center gap-1">
                    Read <ArrowRight size={12} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      ) : null}

      {/* ============ 8.5. LOCATIONS (five VidaVital clinic markets) ============ */}
      <HomeLocationsSection />

      {/* ============ 9. FAQ ============ */}
      <section
        className="bg-[color:var(--vv-alt)] border-y border-stone-200"
        data-testid="home-faq"
      >
        <div className="vv-container py-20">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <div className="vv-hero-eyebrow mb-2">FAQ</div>
            <h2 className="font-display text-3xl md:text-4xl">
              Frequently asked questions.
            </h2>
          </div>
          <div className="max-w-3xl mx-auto space-y-3">
            {HOMEPAGE_FAQ.map((f) => (
              <details
                key={f.q}
                className="group rounded-2xl bg-white border border-stone-200"
              >
                <summary className="cursor-pointer p-5 font-medium text-stone-900 list-none flex items-start justify-between gap-3">
                  <span>{f.q}</span>
                  <span className="text-[color:var(--vv-teal)] font-bold text-lg select-none group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="px-5 pb-5 text-stone-700 leading-relaxed text-sm">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ============ 10. FINAL CONSULTATION CTA ============ */}
      <section className="vv-container py-20" data-testid="home-final-cta">
        <div className="rounded-3xl bg-[color:var(--vv-ink)] text-white p-10 md:p-16 flex flex-col md:flex-row md:items-center gap-8 justify-between">
          <div>
            <MapPin className="text-[color:var(--vv-teal)]" />
            <h2 className="font-display text-3xl md:text-4xl mt-3">
              Ready to see whether peptide therapy is right for you?
            </h2>
            <p className="mt-3 text-stone-300 max-w-xl">
              Meet with one of our licensed medical providers — in person or
              via telemedicine where legally appropriate. If clinically
              appropriate, we'll create a personalized treatment plan and
              coordinate your care.
            </p>
          </div>
          <ScheduleConsultationCTA
            location="home-final-cta"
            variant="gold"
            size="lg"
          />
        </div>
        <div className="mt-10"><Disclaimer /></div>
      </section>
    </div>
  );
}
